import React, { useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'
import './tokens.css'
import './app.css'
import App from './App.jsx'
import {
  activeAccount,
  authEnabled,
  clearSignedOut,
  initAuth,
  signedOutDeliberately,
  signIn,
} from './lib/auth.js'

// Already sent to Microsoft once this browser session. Coming back still
// without an account means the sign-in did not take -- consent refused, the
// account is not in the tenant, the clock is wrong -- and sending them again
// would bounce between two servers forever, with the address bar flickering and
// no way to read the error. So the second attempt is a button somebody presses.
const ATTEMPTED = 'gtm.auth.attempted'

// Sign-in has to resolve before the app makes its first API call, otherwise the
// opening screen fires a dozen unauthenticated requests and shows a dozen
// errors. With auth off this renders straight through.
function Boot() {
  const [state, setState] = useState(authEnabled ? 'checking' : 'ready')
  const [error, setError] = useState(null)

  useEffect(() => {
    // Built without auth. Check that the API agrees before running: a bundle
    // built with VITE_AUTH_ENABLED unset loads perfectly and sends no token, so
    // against an API that requires one the only symptom is every screen failing
    // at once -- and against an API that does not, there is no symptom at all
    // and every write is attributed to one shared identity. This has happened:
    // .dockerignore excluded web/.env from the build context.
    if (!authEnabled) {
      fetch('/api/health')
        .then((r) => r.json())
        .then((health) => {
          if (health?.auth?.enabled) {
            setError(
              'this front end was built with VITE_AUTH_ENABLED unset, but the API requires ' +
                'a token. Rebuild it: `make up`. A restart is not enough -- Vite compiles ' +
                'these values into the bundle.',
            )
            setState('error')
          }
        })
        .catch(() => {
          // The API being unreachable is the app's own problem to report, on
          // the screen the person asked for. Not this gate's.
        })
      return
    }

    const fail = (err) => {
      setError(err.message)
      setState('error')
    }

    initAuth()
      .then((account) => {
        if (account || activeAccount()) {
          sessionStorage.removeItem(ATTEMPTED)
          return setState('ready')
        }
        // Nothing here is readable without an account, so there is nothing to
        // show a signed-out person but the sign-in itself. Go there rather than
        // asking them to press a button whose only option is to press it.
        if (sessionStorage.getItem(ATTEMPTED) || signedOutDeliberately()) {
          return setState('anonymous')
        }
        // StrictMode mounts twice in development, so this runs twice; signIn
        // collapses concurrent redirects, so the second is a no-op.
        sessionStorage.setItem(ATTEMPTED, '1')
        setState('redirecting')
        signIn().catch(fail)
      })
      .catch(fail)
  }, [])

  if (state === 'checking' || state === 'redirecting') {
    return <div className="boot">Taking you to Microsoft to sign in...</div>
  }

  if (state === 'error') {
    return (
      <div className="boot">
        <p className="err">Sign-in is misconfigured: {error}</p>
        <p>docs/AUTH.md has the setup and what each failure means.</p>
      </div>
    )
  }

  // Reached two ways: a sign-in that came back without an account, and a
  // deliberate sign-out. Both want the same thing -- a deliberate press, not
  // another automatic redirect.
  if (state === 'anonymous') {
    const retry = () => {
      sessionStorage.removeItem(ATTEMPTED)
      clearSignedOut()
      signIn().catch((err) => {
        setError(err.message)
        setState('error')
      })
    }
    return (
      <div className="boot">
        <h1>Chemicals GTM</h1>
        <p>Sign in with your InsightsHIGH Microsoft account.</p>
        <button type="button" className="primary" onClick={retry}>
          Sign in with Microsoft
        </button>
      </div>
    )
  }

  return <App />
}

createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <Boot />
  </React.StrictMode>,
)
